import {Action, Button, ButtonGroup, Dialog, Flag, useDialog} from 'platform/components';
import {Box, Show, Space, Spinner, Text, useDevice, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {useEffect} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {usePartialUpdateVehicleMutation} from '@dms/api/sales';
import {FullVehicle, FullVehicleResponseBody, GetManufactureYearApiResponse} from '@dms/api/shared';
import {getApiErrorMessages} from '@dms/api/utils';
import {cebiaApi} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {handleApiError, Widget} from '@dms/shared';

import {
  buildArray,
  composePath,
  EMPTY_PLACEHOLDER,
  suffixTestId,
  TestIdProps,
  useNavigate,
} from 'shared/web';

import {CebiaWidgetAlert} from './components/CebiaWidgetAlert';
import {CebiaWidgetLogo} from './components/CebiaWidgetLogo';
import {useProductionYearWidgetState} from './hooks/useProductionYearWidgetState';

interface CebiaProductionYearVehicleParams {
  id?: string;
  vin?: string;
  manufacturedOnMonth?: number;
  manufacturedOnYear?: number;
}

interface CebiProductionYearWidgetProps extends TestIdProps {
  vehicle: FullVehicle | CebiaProductionYearVehicleParams | FullVehicleResponseBody;
  onSuccess?: (response?: GetManufactureYearApiResponse) => void;
}
export function CebiaProductionYearWidget(props: CebiProductionYearWidgetProps) {
  const {vehicle, onSuccess} = props;
  const formatDateTime = useDateTimeFormatter();
  const navigate = useNavigate();
  const device = useDevice();
  const isCompactLayout = device === 'mobile' || device === 'tablet';
  const {
    apiResponse,
    errorMessages,
    lastChecked,
    setApiResponse,
    setErrorMessages,
    setLastChecked,
  } = useProductionYearWidgetState();
  const [isDialogOpen, openDialog, closeDialog] = useDialog();
  const [getManufactureYear, {isFetching}] = cebiaApi.endpoints.getManufactureYear.useLazyQuery();
  const [partialUpdateVehicle] = usePartialUpdateVehicleMutation();

  const {id: vehicleId, vin, manufacturedOnMonth, manufacturedOnYear} = vehicle;

  useEffect(
    function resetApiResponseOnVinChange() {
      setApiResponse(undefined);
    },
    [vin] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  const handleModalConfirm = (apiResponse?: GetManufactureYearApiResponse) => {
    handlePatchManufactureDate(apiResponse);
    closeDialog();
  };

  const onCebiaWidgetAlertCallback = () => {
    if (!vehicleId) {
      return;
    }
    navigate(composePath(vehiclesRoutes.editV2, {params: {id: vehicleId}}));
  };

  const handlePatchManufactureDate = async (apiResponse?: GetManufactureYearApiResponse) => {
    if (vehicleId && apiResponse) {
      await partialUpdateVehicle({
        vehicleId,
        patchVehicleRequestBody: apiResponse,
      })
        .unwrap()
        .catch(handleApiError);
    }

    onSuccess?.(apiResponse);
    setLastChecked(new Date());
    setApiResponse(undefined);
  };

  // TODO: refactor, it's ugly
  const handleSubmit = () => {
    setErrorMessages(undefined);
    setApiResponse(undefined);

    if (!vehicleId || !vin) {
      return;
    }

    getManufactureYear({vin, vehicleId})
      .unwrap()
      .then((response) => {
        setApiResponse(response);

        if (
          (isNotNil(manufacturedOnMonth) &&
            manufacturedOnMonth !== response?.manufacturedOnMonth) ||
          (isNotNil(manufacturedOnYear) && manufacturedOnYear !== response?.manufacturedOnYear)
        ) {
          openDialog();
          return;
        }

        // vehicle manufacture won't be overridden so we can fill them in straight away
        handlePatchManufactureDate(response);
      })
      .catch((e: Error) =>
        handleApiError(e, {callback: ({error}) => setErrorMessages(getApiErrorMessages(error))})
      );
  };

  const hasLastChecked = isNotNil(lastChecked);

  const widgetActions = buildArray<Action>()
    .when(hasLastChecked, {
      type: 'custom',
      component: (
        <Box width={isCompactLayout ? '100%' : undefined}>
          <Flag
            colorScheme="green"
            label={i18n.t('general.labels.checked')}
            size="large"
            isFullWidth={isCompactLayout}
            data-testid={suffixTestId('cebiaProductionYearWidget-checked', props)}
          />
        </Box>
      ),
    })
    .when(hasLastChecked, {
      type: 'button',
      variant: 'secondary',
      leftIcon: 'action/autorenew',
      isDisabled: !vin,
      onClick: handleSubmit,
      title: i18n.t('general.actions.update'),
      'data-testid': suffixTestId('cebiaProductionYearWidget-update', props),
    })
    .whenNot(hasLastChecked, {
      type: 'button',
      variant: 'secondary',
      isDisabled: !vin,
      onClick: handleSubmit,
      title: i18n.t('general.actions.check'),
      'data-testid': suffixTestId('cebiaProductionYearWidget-check', props),
    });

  return (
    <VStack spacing={2}>
      <Widget.Card data-testid={suffixTestId('cebiaProductionYearWidget', props)}>
        <Widget.Header
          logo={<CebiaWidgetLogo alt={i18n.t('page.integrations.labels.cebiaRokvy')} />}
          title={i18n.t('page.integrations.labels.cebiaProductionYearTitle')}
          subtitle={
            <VStack spacing={2}>
              <Text size="xSmall">
                {i18n.t('page.integrations.labels.cebiaProductionYearWidgetDescription')}
              </Text>
              <Show when={lastChecked}>
                <Text color="tertiary" size="small">
                  {i18n.t('general.labels.checkedOn')}{' '}
                  {lastChecked ? formatDateTime('dateTimeShort', lastChecked) : null}
                </Text>
              </Show>
            </VStack>
          }
          actions={widgetActions}
          data-testid={suffixTestId('cebiaProductionYearWidget', props)}
        />
      </Widget.Card>
      <Show when={!vin}>
        <CebiaWidgetAlert
          colorScheme="warning"
          message={i18n.t('entity.vehicle.notifications.vinIsRequired')}
          actionText={i18n.t('entity.vehicle.labels.editVehicle')}
          actionCallback={onCebiaWidgetAlertCallback}
        />
      </Show>
      <Show
        when={apiResponse?.manufacturedOnYear === null && apiResponse?.manufacturedOnMonth === null}
      >
        <CebiaWidgetAlert
          colorScheme="warning"
          message={i18n.t('entity.vehicle.notifications.noProductionYearForVIN', {vin})}
        />
      </Show>
      <Show when={errorMessages}>
        <CebiaWidgetAlert
          colorScheme="error"
          message={Array.isArray(errorMessages) ? errorMessages.join(', ') : errorMessages}
        />
      </Show>
      <Show when={isFetching}>
        <Spinner variant="overlay" />
      </Show>

      <Dialog
        isOpen={isDialogOpen}
        onClose={closeDialog}
        data-testid={suffixTestId('cebiaProductionYearWidget-dialog', props)}
      >
        {i18n.t('entity.vehicle.notifications.productionYearOverride', {
          sourceMonth: manufacturedOnMonth || EMPTY_PLACEHOLDER,
          sourceYear: manufacturedOnYear || EMPTY_PLACEHOLDER,
          targetMonth: apiResponse?.manufacturedOnMonth ?? EMPTY_PLACEHOLDER,
          targetYear: apiResponse?.manufacturedOnYear ?? EMPTY_PLACEHOLDER,
        })}

        <Space vertical={4} />
        <ButtonGroup align="right">
          <Button
            variant="secondary"
            onClick={closeDialog}
            title={i18n.t('general.actions.cancel')}
            data-testid={suffixTestId('cebiaProductionYearWidgetDialog-cancel', props)}
          />
          <Button
            variant="danger"
            onClick={() => handleModalConfirm(apiResponse)}
            title={i18n.t('general.notifications.replaceButton')}
            data-testid={suffixTestId('cebiaProductionYearWidgetDialog-confirm', props)}
          />
        </ButtonGroup>
      </Dialog>
    </VStack>
  );
}
